import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { type ColumnDef } from "@tanstack/react-table";
import {
  Search,
  SlidersHorizontal,
  LayoutGrid,
  Table as TableIcon,
  Refresh,
  Zap,
  MessageSquare,
  Wifi,
  WifiOff,
  AlertTriangle,
} from "@/components/icons";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { DataTableShell } from "@/components/ui/data-table-shell";
import { DataTable } from "@/components/ui/data-table";
import { refreshSource, useFleetPollInterval, useUnitsRefreshTick, useVisibleInterval } from "@/lib/liveRefresh";

type StatsResponse = {
  messages_per_second?: number;
  wups_per_second?: number;
  active_units?: number;
  unsupported_units?: number;
  state_distribution?: Record<string, number>;
  // Protocol-version fleet breakdown (from /api/stats). legacy_v0_count answers
  // "do we still have v0 no-checksum chargers?"; legacy_v0_unit_ids is a capped
  // sample of their IDs. JSON keys serialize as strings ("0", "9", "10").
  protocol_counts?: Record<string, number>;
  legacy_v0_count?: number;
  legacy_v0_unit_ids?: string[];
  // Factory-default ("amnesia") quarantine: chargers whose firmware lost the
  // provisioned identity and report the shared factory ID. endpoints ~= count
  // of distinct physical devices (by source ip:port); they get replies but are
  // never registered (no sessions/billing).
  factory_default_endpoints?: number;
  factory_default_messages?: number;
  // Currently EFFECTIVE amps sent to amnesia devices (config default or the
  // runtime override from /api/factory-default/limits).
  factory_default_online_limit?: number | null;
  factory_default_offline_limit?: number | null;
  // Per-IP amnesia device rows (grouped by source IP; ports overcount because
  // LTE modems get a new port per reconnect). suspected_* = a real unit that
  // went silent whose last known IP matches — likely this device's identity
  // before the firmware wiped it.
  factory_default_devices?: Array<{
    ip: string;
    last_seen: string;
    endpoints: number;
    suspected_unit_id?: string;
    suspected_unit_name?: string | null;
    suspected_last_seen?: string;
  }>;
};

type Unit = {
  unit_id: string;
  name: string;
  is_online: boolean;
  state_name: string;
  voltage: number;
  current: number;
  session_energy: number; // Wh? (у тебя было /1000 -> kWh)
  power_factor: number;
  location?: string;
  product_class_short?: string;
  protocol_unsupported?: boolean;
  protocol_version?: string | number;
};

type UnitsResponse = {
  units: Unit[];
  total: number;
  no_units_assigned?: boolean;
};

type ViewMode = "tiles" | "table";

const LS_VIEW = "dashboardViewMode_v2";
const LS_SORT = "dashboardSort_v2";
const LS_STATE = "dashboardState_v2";

const PAGE_SIZE_TABLE = 100;

function formatNum(n: number | undefined, digits = 0) {
  const v = typeof n === "number" && isFinite(n) ? n : 0;
  return v.toFixed(digits);
}

function kwhFromSessionEnergy(sessionEnergy: number) {
  // у тебя было (unit.session_energy / 1000).toFixed(2)
  return (sessionEnergy / 1000).toFixed(2);
}

/** Compact relative time for the Factory-ID devices table ("3m ago"). */
function relTime(isoStr: string | null | undefined): string {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return "—";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function statusBadgeProps(state: string, isOnline: boolean): { variant: "success" | "error" | "warning" | "info" | "primary" | "neutral"; outline?: boolean } {
  if (!isOnline) return { variant: "neutral" };
  const s = (state || "").toLowerCase();
  if (s === "charging" || s === "charging_simple") return { variant: "success" };
  if (s === "error") return { variant: "error" };
  if (s === "ready") return { variant: "warning", outline: true };
  if (s === "connected") return { variant: "info", outline: true };
  if (s === "standby") return { variant: "primary", outline: true };
  if (s.startsWith("unsupported")) return { variant: "warning" };
  if (s === "blocked") return { variant: "neutral" };
  return { variant: "neutral", outline: true };
}

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n));
}

const adminTableColumns: ColumnDef<Unit, any>[] = [
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => (
      <Badge {...statusBadgeProps(row.original.state_name, row.original.is_online)}>
        {row.original.is_online ? row.original.state_name : "offline"}
      </Badge>
    ),
  },
  {
    id: "unit",
    header: "Unit",
    cell: ({ row }) => {
      const raw = row.original.name || "";
      const hasCustomName = raw && raw !== `Unit ${row.original.unit_id}`;
      return (
        <div className="overflow-hidden">
          {hasCustomName && (
            <div className="font-semibold truncate">
              {raw}
              {row.original.protocol_unsupported ? (
                <Badge variant="warning" className="ml-2">⚠ v{row.original.protocol_version}</Badge>
              ) : null}
            </div>
          )}
          {!hasCustomName && row.original.protocol_unsupported && (
            <Badge variant="warning" className="mb-0.5">⚠ v{row.original.protocol_version}</Badge>
          )}
          <div className="font-mono text-xs overflow-hidden text-ellipsis text-left opacity-60" dir="rtl">{row.original.unit_id}</div>
        </div>
      );
    },
  },
  {
    accessorKey: "product_class_short",
    header: "Product",
    cell: ({ getValue }) => <span className="text-sm">{getValue() || "Unknown"}</span>,
  },
  {
    accessorKey: "voltage",
    header: "Voltage",
    cell: ({ getValue }) => `${getValue().toFixed(1)}V`,
  },
  {
    accessorKey: "current",
    header: "Current",
    cell: ({ getValue }) => `${getValue().toFixed(1)}A`,
  },
  {
    accessorKey: "session_energy",
    header: "Session Energy",
    cell: ({ getValue }) => <span className="font-semibold">{kwhFromSessionEnergy(getValue())} kWh</span>,
  },
  {
    accessorKey: "power_factor",
    header: "Power Factor",
    cell: ({ getValue }) => getValue().toFixed(2),
  },
  {
    accessorKey: "location",
    header: "Location",
    cell: ({ getValue }) => <span className="text-sm max-w-[180px] truncate block">{getValue() || "Unknown"}</span>,
  },
  {
    id: "actions",
    header: () => <span className="text-right block">Actions</span>,
    cell: ({ row }) => (
      <div className="text-right">
        <Link className="text-primary underline-offset-4 hover:underline" to={`/unit/${encodeURIComponent(row.original.unit_id)}`}>
          View →
        </Link>
      </div>
    ),
  },
];

export function AdminDashboardPage() {
  // ----------------------------
  // View mode
  // ----------------------------
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const v = localStorage.getItem(LS_VIEW);
    return v === "table" ? "table" : "tiles";
  });

  useEffect(() => {
    localStorage.setItem(LS_VIEW, viewMode);
  }, [viewMode]);

  // ----------------------------
  // Controls
  // ----------------------------
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState<string[]>(() => {
    const raw = localStorage.getItem(LS_STATE);
    if (!raw) return [];
    try { const p = JSON.parse(raw); if (Array.isArray(p)) return p; } catch {}
    return [];
  });
  const [sortBy, setSortBy] = useState(() => localStorage.getItem(LS_SORT) || "name");

  useEffect(() => localStorage.setItem(LS_STATE, JSON.stringify(stateFilter)), [stateFilter]);
  useEffect(() => localStorage.setItem(LS_SORT, sortBy), [sortBy]);

  const stateParam = stateFilter.join(",");
  const queryKey = useMemo(() => {
    return {
      search: search.trim(),
      state: stateParam,
      sort: sortBy,
    };
  }, [search, stateParam, sortBy]);

  // ----------------------------
  // Stats (poll)
  // ----------------------------
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [statsErr, setStatsErr] = useState<string | null>(null);

  // Factory-ID devices panel: lightweight client-side filter over the rows
  // already in the stats payload — matches IP or suspected unit id/name.
  const [factoryFilter, setFactoryFilter] = useState("");
  // Runtime control of the quarantine current limit (sets online AND offline
  // to the same value — there's no per-device state to differentiate them).
  const [factoryLimitInput, setFactoryLimitInput] = useState("");
  const [factoryLimitSaving, setFactoryLimitSaving] = useState(false);
  const effectiveFactoryLimit = stats?.factory_default_online_limit;
  async function saveFactoryLimit() {
    const amps = Number(factoryLimitInput);
    if (!Number.isInteger(amps) || amps < 0 || amps > 80) {
      toast.error("Limit must be an integer 0–80 A");
      return;
    }
    if (!confirm(
      `Set the current limit for ALL factory-ID (amnesia) devices to ${amps} A?` +
      (amps === 0 ? " 0 A BLOCKS charging on them entirely." : "") +
      " Takes effect on the chargers within ~30s."
    )) return;
    setFactoryLimitSaving(true);
    try {
      const res = await fetch("/api/factory-default/limits", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ online: amps, offline: amps }),
      });
      if (!res.ok) throw new Error((await res.json())?.error || `http ${res.status}`);
      toast.success(`Factory-ID limit set to ${amps} A`);
      setFactoryLimitInput("");
      fetchStats();
    } catch (e: any) {
      toast.error(e?.message || "Failed to set limit");
    } finally {
      setFactoryLimitSaving(false);
    }
  }
  const filteredFactoryDevices = useMemo(() => {
    const rows = stats?.factory_default_devices ?? [];
    const f = factoryFilter.trim().toLowerCase();
    if (!f) return rows;
    return rows.filter(
      (d) =>
        d.ip.toLowerCase().includes(f) ||
        (d.suspected_unit_id ?? "").toLowerCase().includes(f) ||
        (d.suspected_unit_name ?? "").toLowerCase().includes(f)
    );
  }, [stats?.factory_default_devices, factoryFilter]);

  async function fetchStats() {
    try {
      setStatsErr(null);
      const res = await fetch("/api/stats");
      if (!res.ok) throw new Error(`stats http ${res.status}`);
      const data = (await res.json()) as StatsResponse;
      setStats(data);
    } catch (e: any) {
      setStatsErr(e?.message || "stats error");
    }
  }

  useEffect(() => {
    fetchStats();
  }, []);
  // stats:snapshot is one small GET; keep its cadence but not in a hidden tab.
  useVisibleInterval(fetchStats, 2000);

  // ----------------------------
  // Units: tiles mode (virtual rows)
  // ----------------------------
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [containerW, setContainerW] = useState<number>(1100);

  // measure container width (for columns)
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setContainerW(el.clientWidth || 1100);
    });
    ro.observe(el);
    setContainerW(el.clientWidth || 1100);
    return () => ro.disconnect();
  }, []);

  const itemMinW = 320; // card min width
  const gap = 16;       // approx
  const cols = clamp(Math.floor((containerW + gap) / (itemMinW + gap)), 1, 5);

  const [totalUnits, setTotalUnits] = useState(0);
  const [noAccess, setNoAccess] = useState(false);

  // sparse array storage for tiles
  const [tilesUnits, setTilesUnits] = useState<Array<Unit | undefined>>([]);
  const [tilesLoading, setTilesLoading] = useState(false);
  const [tilesErr, setTilesErr] = useState<string | null>(null);

  // reset on filter change
  useEffect(() => {
    setTotalUnits(0);
    setNoAccess(false);
    setTilesUnits([]);
    setTilesErr(null);
  }, [queryKey]);

  const rowCount = Math.ceil((totalUnits || 0) / cols) || 0;
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 236, // ~ card height + gap
    overscan: 6,
  });

  async function fetchUnitsRange(start: number, end: number, source = "initial") {
    const params = new URLSearchParams({
      start: String(start),
      end: String(end),
      search: queryKey.search,
      state: queryKey.state,
      sort: queryKey.sort,
    });

    const res = await fetch(`/api/units?${params.toString()}`, {
      headers: { "X-Refresh-Source": source },
    });
    if (!res.ok) throw new Error(`units http ${res.status}`);
    return (await res.json()) as UnitsResponse;
  }

  // fetch visible window (tiles)
  useEffect(() => {
    if (viewMode !== "tiles") return;
    if (!rowCount) {
      // need initial fetch
      (async () => {
        try {
          setTilesLoading(true);
          setTilesErr(null);
          const data = await fetchUnitsRange(0, cols * 60); // initial chunk
          if (data.no_units_assigned) {
            setNoAccess(true);
            setTotalUnits(0);
            setTilesUnits([]);
            return;
          }
          setNoAccess(false);
          setTotalUnits(data.total || 0);
          setTilesUnits((prev) => {
            const next = new Array<Unit | undefined>(data.total || 0);
            for (let i = 0; i < (data.units?.length || 0); i++) next[i] = data.units[i];
            return next;
          });
        } catch (e: any) {
          setTilesErr(e?.message || "Failed to load units");
        } finally {
          setTilesLoading(false);
        }
      })();
      return;
    }

    // fetch based on visible rows
    const v = rowVirtualizer.getVirtualItems();
    if (!v.length) return;

    const firstRow = v[0].index;
    const lastRow = v[v.length - 1].index;

    const startIdx = firstRow * cols;
    const endIdx = Math.min(totalUnits, (lastRow + 1) * cols + cols * 12); // buffer

    // if range already filled enough, skip
    const needs = (() => {
      if (!tilesUnits.length) return true;
      for (let i = startIdx; i < endIdx; i++) {
        if (!tilesUnits[i]) return true;
      }
      return false;
    })();

    if (!needs || tilesLoading || noAccess) return;

    (async () => {
      try {
        setTilesLoading(true);
        setTilesErr(null);
        const data = await fetchUnitsRange(startIdx, endIdx);
        if (data.no_units_assigned) {
          setNoAccess(true);
          setTotalUnits(0);
          setTilesUnits([]);
          return;
        }
        setNoAccess(false);
        setTotalUnits(data.total || 0);

        setTilesUnits((prev) => {
          const total = data.total || prev.length || 0;
          const next = prev.length === total ? [...prev] : new Array<Unit | undefined>(total);
          // keep existing if sizes differ
          if (prev.length && prev.length !== total) {
            for (let i = 0; i < Math.min(prev.length, total); i++) next[i] = prev[i];
          }
          const incoming = data.units || [];
          for (let i = 0; i < incoming.length; i++) {
            next[startIdx + i] = incoming[i];
          }
          return next;
        });
      } catch (e: any) {
        setTilesErr(e?.message || "Failed to load units");
      } finally {
        setTilesLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, rowCount, cols, totalUnits, queryKey, rowVirtualizer.getVirtualItems().length]);

  // Refresh the visible tiles (like old virtualScroll.refreshVisible) on the
  // shared fleet-list policy: on WS-driven invalidations, and otherwise on the
  // slow fallback timer, never while the tab is hidden. Only one view mode is
  // mounted at a time, so this is the page's only units refresh loop.
  const pollInterval = useFleetPollInterval();
  const unitsRefreshTick = useUnitsRefreshTick();
  const refreshVisibleTiles = async () => {
    try {
      const v = rowVirtualizer.getVirtualItems();
      if (!v.length || noAccess) return;
      const firstRow = v[0].index;
      const lastRow = v[v.length - 1].index;
      const startIdx = firstRow * cols;
      const endIdx = Math.min(totalUnits, (lastRow + 1) * cols);

      const data = await fetchUnitsRange(startIdx, endIdx, refreshSource(true));
      if (data.no_units_assigned) return;

      setTilesUnits((prev) => {
        const next = [...prev];
        const incoming = data.units || [];
        for (let i = 0; i < incoming.length; i++) next[startIdx + i] = incoming[i];
        return next;
      });
    } catch {
      // silent: refresh is best-effort
    }
  };
  useVisibleInterval(refreshVisibleTiles, viewMode === "tiles" ? pollInterval : null);
  useEffect(() => {
    if (viewMode === "tiles" && unitsRefreshTick > 0) void refreshVisibleTiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitsRefreshTick]);

  // ----------------------------
  // Units: table mode (paged)
  // ----------------------------
  const [tableUnits, setTableUnits] = useState<Unit[]>([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [tableOffset, setTableOffset] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableErr, setTableErr] = useState<string | null>(null);

  async function fetchTable(reset: boolean) {
    if (tableLoading) return;
    setTableLoading(true);
    setTableErr(null);

    try {
      const offset = reset ? 0 : tableOffset;
      const data = await fetchUnitsRange(offset, offset + PAGE_SIZE_TABLE);
      if (data.no_units_assigned) {
        setNoAccess(true);
        setTableUnits([]);
        setTableTotal(0);
        return;
      }
      setNoAccess(false);
      setTableTotal(data.total || 0);

      if (reset) {
        setTableUnits(data.units || []);
        setTableOffset(PAGE_SIZE_TABLE);
      } else {
        setTableUnits((prev) => prev.concat(data.units || []));
        setTableOffset(offset + PAGE_SIZE_TABLE);
      }
    } catch (e: any) {
      setTableErr(e?.message || "Failed to load table");
    } finally {
      setTableLoading(false);
    }
  }

  // reset table when filters change or mode flips to table
  useEffect(() => {
    if (viewMode !== "table") return;
    setTableUnits([]);
    setTableTotal(0);
    setTableOffset(0);
    setTableErr(null);
    fetchTable(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, queryKey]);

  // ----------------------------
  // Apply / refresh
  // ----------------------------
  function onApply() {
    // For tiles, we already reactively fetch based on queryKey.
    // But "Apply" should reset scroll to top and force initial fetch.
    if (viewMode === "tiles") {
      parentRef.current?.scrollTo({ top: 0 });
      setTotalUnits(0);
      setTilesUnits([]);
      setTilesErr(null);
    } else {
      fetchTable(true);
    }
  }

  function headerLabel(): string {
    if (noAccess) return "No Access";
    if (stateFilter.length) return `${stateFilter.join(", ")} units`;
    if (search.trim()) return "Filtered units";
    return "All units";
  }

  const countLabel = useMemo(() => {
    const t = viewMode === "table" ? tableTotal : totalUnits;
    if (!t) return "";
    return `(${t})`;
  }, [viewMode, tableTotal, totalUnits]);

  // ----------------------------
  // UI
  // ----------------------------
  const dist = stats?.state_distribution || {};
  const chargingCount = dist["charging"] || 0;
  const offlineCount = dist["offline"] || 0;

  return (
      <div className="space-y-5 pt-[86px]">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">EVSE Management Dashboard</h1>
            <div className="text-sm opacity-70">
              Live overview of units, sessions and telemetry
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetchStats} title="Refresh stats">
              <Refresh className="h-4 w-4" />
              Refresh
            </Button>
            <Button size="sm" asChild>
              <Link to="/sessions">Open Sessions</Link>
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase opacity-60">Messages/sec</div>
                <MessageSquare className="h-4 w-4 opacity-60" />
              </div>
              <div className="text-3xl font-semibold">{formatNum(stats?.messages_per_second, 0)}</div>
              {statsErr ? <div className="text-xs text-error">{statsErr}</div> : <div className="text-xs opacity-60">telemetry pipeline</div>}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase opacity-60">WUPs/sec</div>
                <Zap className="h-4 w-4 opacity-60" />
              </div>
              <div className="text-3xl font-semibold">{formatNum(stats?.wups_per_second, 1)}</div>
              <div className="text-xs opacity-60">wake-up traffic</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase opacity-60">Active Units</div>
                <Wifi className="h-4 w-4 opacity-60" />
              </div>
              <div className="text-3xl font-semibold">{stats?.active_units || 0}</div>
              <div className="text-xs opacity-60">online recently</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase opacity-60">Charging</div>
                <Badge variant="success" outline>Live</Badge>
              </div>
              <div className="text-3xl font-semibold">{chargingCount}</div>
              <div className="text-xs opacity-60">currently charging</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase opacity-60">Offline</div>
                <WifiOff className="h-4 w-4 opacity-60" />
              </div>
              <div className="text-3xl font-semibold">{offlineCount}</div>
              <div className="text-xs opacity-60">no heartbeat</div>
            </CardContent>
          </Card>

          <Card className="bg-warning/10 shadow border border-warning/30">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase opacity-70">Unsupported</div>
                <AlertTriangle className="h-4 w-4 opacity-70" />
              </div>
              <div className="text-3xl font-semibold text-warning">{stats?.unsupported_units || 0}</div>
              <div className="text-xs opacity-70">protocol mismatch</div>
            </CardContent>
          </Card>

          {/* Factory-default (amnesia) devices — shown only while any exist.
              Count = distinct source endpoints reporting the shared factory ID
              (quarantined: replied-to, never registered). Disappears once the
              fleet is re-provisioned. Full per-device panel: planned. */}
          {(stats?.factory_default_endpoints ?? 0) > 0 && (
            <Card className="bg-warning/10 shadow border border-warning/30">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase opacity-70">Amnesia Devices</div>
                  <AlertTriangle className="h-4 w-4 opacity-70" />
                </div>
                <div className="text-3xl font-semibold text-warning">{stats?.factory_default_endpoints}</div>
                <div className="text-xs opacity-70">
                  factory-ID chargers, quarantined ({formatNum(stats?.factory_default_messages, 0)} msgs)
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Protocol version breakdown — surfaces whether any legacy v0
            (no-checksum) chargers remain in the fleet. Data from /api/stats. */}
        <Card className={(stats?.legacy_v0_count ?? 0) > 0 ? "bg-warning/10 border border-warning/30" : ""}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase opacity-60">Protocol Versions</div>
              <AlertTriangle className="h-4 w-4 opacity-60" />
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats?.protocol_counts ?? {})
                .sort((a, b) => Number(b[1]) - Number(a[1]))
                .map(([ver, count]) => (
                  <Badge key={ver} variant={ver === "0" ? "warning" : "neutral"} outline>
                    v{ver}: {formatNum(count, 0)}
                  </Badge>
                ))}
              {Object.keys(stats?.protocol_counts ?? {}).length === 0 && (
                <span className="text-xs opacity-60">no data yet</span>
              )}
            </div>
            <div className="mt-3">
              {(stats?.legacy_v0_count ?? 0) > 0 ? (
                <details>
                  <summary className="cursor-pointer text-sm font-semibold text-warning">
                    {stats?.legacy_v0_count} legacy v0 charger{(stats?.legacy_v0_count ?? 0) === 1 ? "" : "s"} — no checksum (click to list)
                  </summary>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    {(stats?.legacy_v0_unit_ids ?? []).map((id) => (
                      <Link
                        key={id}
                        to={`/unit/${encodeURIComponent(id)}`}
                        className="font-mono text-xs text-primary underline-offset-4 hover:underline"
                      >
                        {id}
                      </Link>
                    ))}
                    {(stats?.legacy_v0_count ?? 0) > (stats?.legacy_v0_unit_ids?.length ?? 0) && (
                      <span className="text-xs opacity-60">
                        +{(stats?.legacy_v0_count ?? 0) - (stats?.legacy_v0_unit_ids?.length ?? 0)} more (sample capped)
                      </span>
                    )}
                  </div>
                </details>
              ) : (
                <span className="text-xs opacity-60">No legacy v0 chargers ✓</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Factory-ID (amnesia) devices — the ops "fix me" worklist. One row
            per source IP (ports overcount: LTE modems reconnect on new ports);
            "probably ex-…" = a real unit that went silent whose last known IP
            matches, i.e. this device's likely identity before the firmware
            wiped it. Hidden entirely while the fleet has none. */}
        {(stats?.factory_default_devices?.length ?? 0) > 0 && (
          <Card className="bg-warning/10 border border-warning/30">
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-2 gap-3">
                <div className="text-xs uppercase opacity-60">
                  Factory-ID Devices (
                  {factoryFilter
                    ? `${filteredFactoryDevices.length}/${stats?.factory_default_devices?.length}`
                    : stats?.factory_default_devices?.length}
                  )
                </div>
                {/* Client-side filter — the payload already holds all rows
                    (≤100), so no extra requests. Matches IP or suspect id/name. */}
                <Input
                  className="h-7 w-56 text-xs"
                  placeholder="Filter IP or suspect…"
                  value={factoryFilter}
                  onChange={(e) => setFactoryFilter(e.target.value)}
                />
                {/* Runtime limit control: applied by the UDP quarantine handler
                    within ~30s, survives restarts (Redis-persisted override). */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs opacity-70">
                    Limit:{" "}
                    <span className="font-semibold">
                      {effectiveFactoryLimit != null ? `${effectiveFactoryLimit} A` : "—"}
                    </span>
                  </span>
                  <Input
                    className="h-7 w-16 text-xs"
                    type="number"
                    min={0}
                    max={80}
                    placeholder="A"
                    value={factoryLimitInput}
                    onChange={(e) => setFactoryLimitInput(e.target.value)}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs"
                    onClick={saveFactoryLimit}
                    disabled={factoryLimitSaving || factoryLimitInput === ""}
                  >
                    Set
                  </Button>
                </div>
                <AlertTriangle className="h-4 w-4 opacity-60 shrink-0" />
              </div>
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs opacity-60">
                      <th className="py-1 pr-3 font-medium">IP address</th>
                      <th className="py-1 pr-3 font-medium">Last seen</th>
                      <th className="py-1 pr-3 font-medium">Conns</th>
                      <th className="py-1 font-medium">Suspected identity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFactoryDevices.map((d) => (
                      <tr key={d.ip} className="border-t border-border/50">
                        <td className="py-1.5 pr-3 font-mono text-xs">{d.ip}</td>
                        <td className="py-1.5 pr-3 text-xs">{relTime(d.last_seen)}</td>
                        <td className="py-1.5 pr-3 text-xs">{d.endpoints}</td>
                        <td className="py-1.5 text-xs">
                          {d.suspected_unit_id ? (
                            <>
                              probably ex-{" "}
                              <Link
                                to={`/unit/${encodeURIComponent(d.suspected_unit_id)}`}
                                className="font-mono text-primary underline-offset-4 hover:underline"
                              >
                                {d.suspected_unit_name &&
                                d.suspected_unit_name !== `Unit ${d.suspected_unit_id}`
                                  ? d.suspected_unit_name
                                  : d.suspected_unit_id}
                              </Link>
                              {d.suspected_last_seen && (
                                <span className="opacity-60">
                                  {" "}(real ID last seen {relTime(d.suspected_last_seen)})
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="opacity-50">unknown</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredFactoryDevices.length === 0 && (
                      <tr>
                        <td colSpan={4} className="py-3 text-center text-xs opacity-60">
                          No matches for "{factoryFilter}"
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Controls */}
        <Card>
          <CardContent className="gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-1 min-w-[240px]">
                <Button variant="ghost" size="sm" aria-label="Search">
                  <Search className="h-4 w-4" />
                </Button>
                <input
                    className="h-8 w-full rounded-4xl border border-input bg-input/30 px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    placeholder="Search Unit ID or Name (use ^prefix for starts-with)…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onApply();
                    }}
                />
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    {stateFilter.length === 0
                      ? "All statuses"
                      : stateFilter.length === 1
                        ? stateFilter[0]
                        : "Statuses"}
                    {stateFilter.length > 1 && (
                      <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] leading-4">
                        {stateFilter.length}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {([
                    ["charging", "Charging"],
                    ["standby", "Standby"],
                    ["ready", "Ready"],
                    ["connected", "Connected"],
                    ["blocked", "Blocked"],
                    ["error", "Error"],
                    ["offline", "Offline"],
                    ["unsupported", "Unsupported Protocol"],
                  ] as const).map(([value, label]) => {
                    const active = stateFilter.includes(value);
                    return (
                      <DropdownMenuCheckboxItem
                        key={value}
                        checked={active}
                        onSelect={(e) => {
                          e.preventDefault();
                          setStateFilter(active
                            ? stateFilter.filter((s) => s !== value)
                            : [...stateFilter, value],
                          );
                        }}
                      >
                        {label}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                  {stateFilter.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() => setStateFilter([])}
                        className="text-xs opacity-70"
                      >
                        Clear filters
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger size="sm">
                  <SelectValue placeholder="Sort by Name" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Sort by Name</SelectItem>
                  <SelectItem value="unit_id">Sort by Unit ID</SelectItem>
                  <SelectItem value="state">Sort by State</SelectItem>
                  <SelectItem value="current">Sort by Current</SelectItem>
                  <SelectItem value="power">Sort by Power</SelectItem>
                </SelectContent>
              </Select>

              <ToggleGroup type="single" size="sm" variant="outline" value={viewMode} onValueChange={(v) => v && setViewMode(v as typeof viewMode)}>
                <ToggleGroupItem value="tiles" aria-label="Tile View">
                  <LayoutGrid className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem value="table" aria-label="Table View">
                  <TableIcon className="h-4 w-4" />
                </ToggleGroupItem>
              </ToggleGroup>

              <Button variant="outline" size="sm" onClick={onApply}>
                <SlidersHorizontal className="h-4 w-4" />
                Apply
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">
                {headerLabel()} <span className="opacity-60">{countLabel}</span>
              </div>

              {viewMode === "tiles" && (
                  <div className="text-xs opacity-60">
                    Grid: {cols} cols • Virtualized
                  </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        {noAccess ? (
            <Card>
              <CardContent className="items-center text-center">
                <div className="text-4xl">🔒</div>
                <div className="text-lg font-semibold">No Units Assigned</div>
                <div className="opacity-70">
                  Contact your administrator to get access to charging units.
                </div>
              </CardContent>
            </Card>
        ) : viewMode === "tiles" ? (
            <Card>
              <CardContent className="p-0">
                {/* scroll container */}
                <div
                    ref={parentRef}
                    className="h-[calc(100vh-330px)] min-h-[520px] overflow-auto rounded-2xl"
                >
                  {/* virtual total size */}
                  <div
                      style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: "100%",
                        position: "relative",
                      }}
                  >
                    {rowVirtualizer.getVirtualItems().map((row) => {
                      const start = row.index * cols;
                      const end = Math.min(totalUnits, start + cols);
                      const items = [];
                      for (let i = start; i < end; i++) items.push(tilesUnits[i]);

                      return (
                          <div
                              key={row.key}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                transform: `translateY(${row.start}px)`,
                                padding: 16,
                              }}
                          >
                            <div
                                className="grid gap-4"
                                style={{
                                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                                }}
                            >
                              {items.map((u, idx) =>
                                  u ? (
                                      <UnitCard key={u.unit_id} unit={u} />
                                  ) : (
                                      <Card key={`${row.index}-${idx}`} className="bg-card/60">
                                        <CardContent>
                                          <div className="skeleton h-5 w-2/3" />
                                          <div className="skeleton h-4 w-1/2" />
                                          <div className="mt-4 grid grid-cols-2 gap-2">
                                            <div className="skeleton h-4" />
                                            <div className="skeleton h-4" />
                                            <div className="skeleton h-4" />
                                            <div className="skeleton h-4" />
                                          </div>
                                        </CardContent>
                                      </Card>
                                  )
                              )}
                            </div>
                          </div>
                      );
                    })}
                  </div>

                  {tilesErr ? (
                      <div className="p-6 text-error">{tilesErr}</div>
                  ) : null}

                  {tilesLoading && totalUnits > 0 ? (
                      <div className="p-4 text-center text-sm opacity-60">Loading…</div>
                  ) : null}

                  {!tilesLoading && totalUnits === 0 && !tilesErr ? (
                      <div className="p-10 text-center opacity-70">
                        No units found.
                      </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>
        ) : (
            <DataTableShell
              footer={
                <>
                  <div className="text-sm opacity-70">
                    Showing <span className="font-semibold">{tableUnits.length}</span> of{" "}
                    <span className="font-semibold">{tableTotal}</span>
                  </div>
                  {tableUnits.length < tableTotal ? (
                    <Button
                      variant="outline"
                      disabled={tableLoading}
                      onClick={() => fetchTable(false)}
                    >
                      {tableLoading ? "Loading…" : "Load More"}
                    </Button>
                  ) : (
                    <div className="text-sm opacity-60">All loaded</div>
                  )}
                </>
              }
            >
                {tableErr ? <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive mx-4 mt-3">{tableErr}</div> : null}
                <DataTable
                  columns={adminTableColumns}
                  data={tableUnits}
                  getRowId={(u) => u.unit_id}
                  isLoading={tableLoading && tableUnits.length === 0}
                  emptyMessage="No units found"
                  rowClassName={(u) => (!u.is_online ? "opacity-70" : "")}
                />
            </DataTableShell>
        )}
      </div>
  );
}

function UnitCard({ unit }: { unit: Unit }) {
  const badgeProps = statusBadgeProps(unit.state_name, unit.is_online);
  const product = unit.product_class_short || "Unknown";
  const kwh = kwhFromSessionEnergy(unit.session_energy);

  return (
      <Link
          to={`/unit/${encodeURIComponent(unit.unit_id)}`}
          className="block"
      >
        <Card
            className={[
              "transition",
              "hover:shadow-md hover:-translate-y-[1px]",
              unit.protocol_unsupported ? "border border-warning/40 bg-warning/5" : "",
              !unit.is_online ? "opacity-70 bg-card/40" : "",
            ].join(" ")}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {(!unit.name || unit.name === `Unit ${unit.unit_id}`) ? null : (
                  <div className="font-semibold truncate">
                    {unit.name}
                    {unit.protocol_unsupported ? (
                        <Badge variant="warning" className="ml-2">⚠ v{unit.protocol_version}</Badge>
                    ) : null}
                  </div>
                )}
                {(!unit.name || unit.name === `Unit ${unit.unit_id}`) && unit.protocol_unsupported && (
                  <Badge variant="warning" className="mb-0.5">⚠ v{unit.protocol_version}</Badge>
                )}
                <div className="font-mono text-xs overflow-hidden text-ellipsis text-left opacity-60" dir="rtl">{unit.unit_id}</div>
                <div className="text-xs opacity-60 mt-1">{product}</div>
                {unit.location && (
                  <div className="text-xs opacity-50 truncate mt-0.5">{unit.location}</div>
                )}
              </div>
              <Badge {...badgeProps}>{unit.is_online ? unit.state_name : "offline"}</Badge>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div className="opacity-70">Voltage</div>
              <div className="text-right font-semibold">{unit.voltage.toFixed(1)}V</div>

              <div className="opacity-70">Current</div>
              <div className="text-right font-semibold">{unit.current.toFixed(1)}A</div>

              <div className="opacity-70">Session</div>
              <div className="text-right font-semibold">{kwh} kWh</div>

              <div className="opacity-70">PF</div>
              <div className="text-right font-semibold">{unit.power_factor.toFixed(2)}</div>
            </div>
          </CardContent>
        </Card>
      </Link>
  );
}
